import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { QFormControl, QMultiSelect, QSelect, QSelectPlaceholder, QText } from '@qualio/ui-components';
import { Product } from '../../../types';
import ItemWithIdAndNameLabel from './ItemWithIdAndNameLabel';
import { EMPTY_FILTER_VALUE } from '../../../types/qualityEvent';
import { emptyAwareFilterOption } from './emptyAwareFilterOption';
import { nativeCodeNameFilterOption } from './codeNameFilterOption';
import { CodeNameMultiSelect, CodeNameSelect } from './codeNameSelect';

export const ProductFilter = ({
  isLoadingProducts,
  products,
  isMultiSelect = false,
  isAdditionalEventListFiltersEnabled = false,
  isMultiSelectEventsListEnabled = false,
}: {
  isLoadingProducts: boolean;
  products: Product[];
  isMultiSelect?: boolean;
  isAdditionalEventListFiltersEnabled?: boolean;
  isMultiSelectEventsListEnabled?: boolean;
}) => {
  const { t } = useTranslation();

  const nativeProductsOptions = products.map((product) => ({
    value: product.id,
    label: product.name,
    code: product.displayId,
  }));

  const nativeMultiSelectProductsOptions: Array<{ value: string; label: React.ReactNode; code?: string }> = [
    ...(isAdditionalEventListFiltersEnabled
      ? [{ value: EMPTY_FILTER_VALUE, label: <em>{t('common.labels.emptyProducts')}</em> }]
      : []),
    ...nativeProductsOptions,
  ];

  const productsOptions = products.map((product) => ({
    value: product.id,
    label: <ItemWithIdAndNameLabel itemName={product.name} itemId={product.displayId} setMinWidth={true} />,
  }));

  const multiSelectProductsOptions: Array<{ value: string; label: React.ReactNode }> = [
    ...(isAdditionalEventListFiltersEnabled
      ? [{ value: EMPTY_FILTER_VALUE, label: <em>{t('common.labels.emptyProducts')}</em> }]
      : []),
    ...products.map((product) => ({
      value: product.id,
      label: `${product.displayId} - ${product.name}`,
    })),
  ];

  const productFilter = useCallback((option: any, searchText: string) => {
    const productName = option.data.label.props.itemName;
    const productId = option.data.label.props.itemId;
    return !!(
      productName.toLowerCase().includes(searchText.toLowerCase()) ||
      productId.toLowerCase().includes(searchText.toLowerCase())
    );
  }, []);

  return (
    <QFormControl
      label={isMultiSelect ? t('common.labels.products') : t('common.labels.product')}
      data-cy="product-filter"
      w="300px"
    >
      {isMultiSelect ? (
        isMultiSelectEventsListEnabled ? (
          <Controller
            name={'product_ids'}
            render={({ field: { onChange, value } }) => (
              <QMultiSelect
                options={nativeMultiSelectProductsOptions}
                aria-label="product-filter"
                onChange={(selected) => onChange(selected.map((c) => c.value))}
                value={value ?? []}
                isLoading={isLoadingProducts}
                filterOption={nativeCodeNameFilterOption}
              >
                <QSelectPlaceholder>
                  <QText>{t('common.filters.filterByProduct')}</QText>
                </QSelectPlaceholder>
              </QMultiSelect>
            )}
          />
        ) : (
          <Controller
            name={'product_ids'}
            render={({ field: { onChange, value } }) => (
              <CodeNameMultiSelect
                options={multiSelectProductsOptions}
                aria-label="product-filter"
                onChange={(val) => onChange(val.map((c) => c.value))}
                value={value ?? []}
                isLoading={isLoadingProducts}
                filterOption={emptyAwareFilterOption}
              >
                <QSelectPlaceholder>
                  <QText>{t('common.filters.filterByProduct')}</QText>
                </QSelectPlaceholder>
              </CodeNameMultiSelect>
            )}
          />
        )
      ) : isMultiSelectEventsListEnabled ? (
        <Controller
          name={'product_id'}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <QSelect
              {...field}
              options={nativeProductsOptions}
              filterOption={nativeCodeNameFilterOption}
              onChange={(changeObject) => {
                onChange(changeObject?.value ?? '');
              }}
              aria-label="product-filter"
              isClearable={true}
              value={value ?? ''}
              isLoading={isLoadingProducts}
              key={`event-product-${value}`}
            >
              <QSelectPlaceholder>
                <QText>{t('common.filters.filterByProduct')}</QText>
              </QSelectPlaceholder>
            </QSelect>
          )}
        />
      ) : (
        <Controller
          name={'product_id'}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <CodeNameSelect
              {...field}
              options={productsOptions}
              filterOption={productFilter}
              onChange={(changeObject) => {
                onChange(changeObject?.value ?? '');
              }}
              aria-label="product-filter"
              isClearable={true}
              value={value ?? ''}
              isLoading={isLoadingProducts}
              key={`event-product-${value}`}
            >
              <QSelectPlaceholder>
                <QText>{t('common.filters.filterByProduct')}</QText>
              </QSelectPlaceholder>
            </CodeNameSelect>
          )}
        />
      )}
    </QFormControl>
  );
};

export default ProductFilter;
